import * as React from "react";
import { Icon } from "../actions/Icon";

/**
 * Text field with optional leading icon, trailing hotkey and send button. "accent" is the Ask composer; "mono" is for links and keys.
 * Pass label (and optionally help / error) for a form field: the label is a real <label for>, so
 * the input's accessible name is the label text.
 */
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  icon?: string;
  kbd?: string;
  variant?: "default" | "sunken" | "accent" | "mono";
  size?: "sm" | "md" | "lg";
  /** Show the purple send button (Ask composer) */
  onSend?: () => void;
  trailing?: React.ReactNode;
  wrapperStyle?: React.CSSProperties;
  /** Visible field label above the input, associated with it */
  label?: React.ReactNode;
  /** Help text under the field (form fields only) */
  help?: React.ReactNode;
  /** Error text under the field; replaces help and marks the input invalid (form fields only) */
  error?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { icon, kbd, variant = "default", size = "md", onSend, trailing, className, wrapperStyle, label, help, error, id, ...rest },
  ref,
) {
  const generatedId = React.useId();
  const cls = [
    "gk-input",
    variant !== "default" && "gk-input--" + variant,
    size !== "md" && "gk-input--" + size,
    error && "gk-input--error",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const inner = (
    <>
      {icon && <Icon name={icon} size={variant === "accent" ? 18 : 20} />}
      {/* The input id and described-by are only needed when there is a separate label. */}
      {label === undefined ? (
        <input ref={ref} id={id} {...rest} />
      ) : (
        <input
          ref={ref}
          id={id ?? generatedId}
          aria-invalid={error ? true : undefined}
          aria-describedby={help || error ? (id ?? generatedId) + "-help" : undefined}
          {...rest}
        />
      )}
      {kbd && <span className="gk-kbd">{kbd}</span>}
      {trailing}
      {onSend && (
        <button type="button" className="gk-input__send" aria-label="Send" onClick={onSend}>
          <Icon name="arrow-up" size={18} />
        </button>
      )}
    </>
  );
  if (label === undefined) {
    return (
      <label className={cls} style={wrapperStyle}>
        {inner}
      </label>
    );
  }
  // Form field: <label for> above, a non-label wrapper (two labels would both feed the name),
  // and a help line that always takes space so nothing shifts when an error appears.
  const inputId = id ?? generatedId;
  return (
    <div className="gk-field">
      <label className="gk-field__label" htmlFor={inputId}>
        {label}
      </label>
      <div className={cls} style={wrapperStyle}>
        {inner}
      </div>
      <div id={inputId + "-help"} className={"gk-field__help" + (error ? " gk-field__help--error" : "")}>
        {error ?? help ?? ""}
      </div>
    </div>
  );
});
